import { differenceInCalendarDays, format } from 'date-fns'
import { move } from '@/utils/array-util'
import { type SearchableJobSortField } from '@/features/jobs/list/job-sort-fields'
import {
  type ColumnId,
  exportValue,
  formattedProgressionStep,
} from '@/features/jobs/list/jobs-table-columns'
import { type SearchableJob } from '@/features/jobs/list/SearchableJob'
import {
  inProgressStatuses,
  type JobListPerspective,
  whoseTurn,
} from '@/features/jobs/list/job-list-view'

export type JobListColumnId =
  'primary' | 'progress' | 'waitingOn' | 'due' | 'lastActivity' | 'jobSpecs'

export type AnyJobListColumnId = JobListColumnId | ColumnId

export interface JobListColumn {
  id: AnyJobListColumnId
  header: (perspective: JobListPerspective) => string
  defaultVisible: boolean
  defaultWidth: number
  /**
   * The primary column is how a row is identified, so it can't be hidden.
   */
  locked?: boolean
  sortField?: (perspective: JobListPerspective) => SearchableJobSortField
  alignRight?: boolean
  /**
   * Only offered in the CSV export, never as a table column.
   */
  exportOnly?: boolean
  exportValue: (
    job: SearchableJob,
    perspective: JobListPerspective,
  ) => string | undefined
}

const fixed = (text: string) => () => text

interface LegacyColumnOptions {
  sortField?: SearchableJobSortField
  alignRight?: boolean
}

const legacy = (
  id: ColumnId,
  header: string,
  defaultWidth: number,
  { sortField, alignRight }: LegacyColumnOptions = {},
): JobListColumn => ({
  id,
  header: fixed(header),
  defaultVisible: false,
  defaultWidth,
  sortField: sortField ? () => sortField : undefined,
  alignRight,
  exportValue: (job) => exportValue(id, job),
})

/**
 * Every column the list can show, in display order. Only the first five are
 * on by default; the rest are added through Display.
 */
export const jobListColumns: JobListColumn[] = [
  {
    id: 'primary',
    header: (p) => (p === 'Pro' ? 'Client' : 'Job'),
    defaultVisible: true,
    defaultWidth: 340,
    locked: true,
    sortField: (p) => (p === 'Pro' ? 'ClientChampionName' : 'Title'),
    exportValue: (job, p) => (p === 'Pro' ? clientName(job) : job.displayName),
  },
  {
    id: 'progress',
    header: fixed('Status'),
    defaultVisible: true,
    defaultWidth: 190,
    exportValue: (job) => statusLabel(job),
  },
  {
    id: 'waitingOn',
    header: fixed('Waiting on'),
    defaultVisible: true,
    defaultWidth: 130,
    exportValue: (job, p) => waitingOn(job, p)?.label ?? '',
  },
  {
    id: 'due',
    header: fixed('Due'),
    defaultVisible: true,
    defaultWidth: 130,
    sortField: () => 'DateDeadline',
    exportValue: (job) => job.dateDeadline?.toLocaleDateString(),
  },
  {
    id: 'lastActivity',
    header: fixed('Last activity'),
    defaultVisible: true,
    defaultWidth: 150,
    sortField: () => 'DateLastActivity',
    exportValue: (job) => job.dateLastActivity?.toLocaleString(),
  },
  {
    ...legacy('jobTitle', 'Job title', 240, { sortField: 'Title' }),
    header: (p) => (p === 'Pro' ? 'Job title' : 'Client'),
    sortField: (p) => (p === 'Pro' ? 'Title' : 'ClientChampionName'),
    exportValue: (job, p) => (p === 'Pro' ? job.displayName : clientName(job)),
  },
  legacy('jobShortId', 'Job ID', 130),
  legacy('legendName', 'Job type', 170, { sortField: 'LegendName' }),
  legacy('workspace', 'Workspace', 160, { sortField: 'WorkspaceName' }),
  legacy('status', 'Job status', 130),
  legacy('jobScopeChangeRequestStatus', 'Scope updates', 140),
  legacy('clientTeam', 'Client team', 160, { sortField: 'ClientTeamName' }),
  legacy('clientChampionEmail', 'Client email', 220, {
    sortField: 'ClientChampionEmail',
  }),
  legacy('proChampion', 'Pro', 170, { sortField: 'ProChampionName' }),
  legacy('proChampionEmail', 'Pro email', 220, {
    sortField: 'ProChampionEmail',
  }),
  legacy('isRequestedPro', 'Requested pro', 120),
  legacy('payout', 'Payout', 110, {
    sortField: 'ProviderCutAmount',
    alignRight: true,
  }),
  legacy('total', 'Total', 110, { alignRight: true }),
  legacy('created', 'Created', 120, { sortField: 'DateCreated' }),
  legacy('submitted', 'Submitted', 120, { sortField: 'DateSubmitted' }),
  legacy('claimed', 'Claimed', 120, { sortField: 'DateClaimed' }),
  legacy('completed', 'Completed', 120, { sortField: 'DateCompleted' }),
  legacy('advanceAllowed', 'Advance allowed', 140),
  legacy('lastClientMessageActivity', 'Last client message', 160, {
    sortField: 'DateLastClientMessageActivity',
  }),
  legacy('lastProMessageActivity', 'Last pro message', 160, {
    sortField: 'DateLastProMessageActivity',
  }),
  legacy('lastRelationshipManagerMessageActivity', 'Last RM message', 160, {
    sortField: 'DateLastRelationshipManagerMessageActivity',
  }),
  {
    id: 'jobSpecs',
    header: fixed('Job specs'),
    defaultVisible: false,
    defaultWidth: 0,
    exportOnly: true,
    exportValue: (job) => job.jobScopeItems.join(', '),
  },
]

/**
 * Puts the columns in the user's order. Locked columns stay first, and columns
 * missing from the order, such as ones added since it was saved, go last in
 * their default order.
 */
export function orderColumns(
  columns: readonly JobListColumn[],
  order: readonly AnyJobListColumnId[],
): JobListColumn[] {
  const rank = (column: JobListColumn) => {
    if (column.locked) {
      return -1
    }
    const index = order.indexOf(column.id)
    return index === -1 ? order.length : index
  }
  return [...columns].sort((a, b) => rank(a) - rank(b))
}

/**
 * Moves a column to `toIndex` within `among`, the subset of `order` that the
 * index counts (such as the shown columns), and returns the whole new order.
 * The columns outside `among` keep their places.
 */
export function moveColumnId(
  order: readonly AnyJobListColumnId[],
  among: readonly AnyJobListColumnId[],
  id: AnyJobListColumnId,
  toIndex: number,
): AnyJobListColumnId[] {
  const from = among.indexOf(id)
  if (from === -1) {
    return [...order]
  }
  const moved = [...among]
  move(moved, from, toIndex)
  const slots = new Set(among)
  let next = 0
  return order.map((c) => (slots.has(c) ? moved[next++] : c))
}

export function clientName(job: SearchableJob): string {
  return (
    job.championClient?.displayName ||
    job.clientTeamName ||
    job.clientMembers[0]?.displayName ||
    ''
  )
}

/**
 * The progression step while the job is being worked, otherwise its lifecycle
 * status (a draft or closed job's last step would be misleading).
 */
export function statusLabel(job: SearchableJob): string {
  if (!inProgressStatuses.includes(job.status)) {
    return exportValue('status', job) ?? ''
  }

  return formattedProgressionStep(job) || exportValue('status', job) || ''
}

export function waitingOn(
  job: SearchableJob,
  perspective: JobListPerspective,
): { label: string; isYou: boolean } | null {
  const turn = whoseTurn(job)
  if (!turn) {
    return null
  }
  const isYou =
    (turn === 'Provider' && perspective === 'Pro') ||
    (turn === 'Client' && perspective === 'Client')
  if (isYou) {
    return { label: 'You', isYou }
  }

  return { label: turn === 'Provider' ? 'Pro' : 'Client', isYou }
}

/**
 * How a job's deadline reads in the list. Overdue jobs and the coming week
 * stand out, unless the job is closed.
 */
export function dueLabel(
  job: SearchableJob,
  now: Date,
): {
  text: string
  tone: 'default' | 'warning' | 'danger'
  title: string
} | null {
  const deadline = job.dateDeadline
  if (!deadline || job.status === 'UnderConstruction') {
    return null
  }

  const date = format(deadline, 'MMM d')
  const title = format(deadline, 'PPP')
  if (job.status === 'Closed') {
    return { text: date, tone: 'default', title }
  }

  const days = differenceInCalendarDays(deadline, now)
  if (days < 0) {
    return { text: `${-days}d overdue`, tone: 'danger', title }
  }
  if (days === 0) {
    return { text: 'Today', tone: 'warning', title }
  }
  if (days === 1) {
    return { text: 'Tomorrow', tone: 'warning', title }
  }
  if (days <= 7) {
    return { text: `${date} · ${days}d`, tone: 'warning', title }
  }

  return { text: date, tone: 'default', title }
}
